import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import { FormControlLabel, MenuItem, Switch } from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import NgpToggle from '../../../../../form/NgpToggle';
import NgpSelect from '../../../../../form/NgpSelect';
import NgpTextField from '../../../../../form/NgpTextField';
import NgpPasswordField from '../../../../../form/NgpPasswordField';
import useNgpFormContext from '../../../../../../utilities/NgpFormContextProviderUtilities';
import { useBttCertificateData } from './UseBttCertificateData';
import { SHOULD_DIRTY_TRUE } from '../../../ServiceTestsFormControlConstants';
import { AUTO_COMPLETE_DEFAULT } from '../../../../../../utilities/FormUtilities';
import TestFormSection from '../TestFormSection/TestFormSection';

const FIELD_LONG_WIDTH = 450;

const useStyles = makeStyles((theme) => ({
    dynamicFieldLongWidth: {
        minWidth: FIELD_LONG_WIDTH,
    },
    subSection: {
        paddingLeft: theme.spacing(5),
    },
}));

/**
 * BTT Certificate / Authentication section.
 * Renders client cert auth, server cert verification, and Run As User sub-sections.
 * Visible when testType.bttTest === true || testType.showBttAuth === true.
 */
export default function TestBttCertificateSection({ bttStateRef }) {
    const classes = useStyles();
    const { setValue } = useFormContext();
    const { formData } = useNgpFormContext();
    const { certificates, isLoading } = useBttCertificateData();

    // --- Virtual toggle states (not direct form fields) ---
    const [certAuthEnabled, setCertAuthEnabled] = useState(() => !isEmpty(formData?.testConfig?.auth));
    const [runAsUser, setRunAsUser] = useState(() => formData?.bttUser != null && !isEmpty(formData.bttUser));
    const [defaultLogin, setDefaultLogin] = useState(() => get(formData, 'bttUser.bttUserDefaultLogin', true));

    // Sync toggle state to ref for bttTransform in parent
    useEffect(() => {
        bttStateRef.current = { certAuthEnabled, runAsUser, defaultLogin };
    }, [certAuthEnabled, runAsUser, defaultLogin, bttStateRef]);

    // --- Handlers ---
    const handleCertAuthToggle = ({ target: { checked } }) => {
        setCertAuthEnabled(checked);
        if (checked) {
            setValue('testConfig.auth', { cert: { _id: '' } }, SHOULD_DIRTY_TRUE);
        } else {
            setValue('testConfig.auth', null, SHOULD_DIRTY_TRUE);
        }
    };

    const handleRunAsUserToggle = ({ target: { checked } }) => {
        setRunAsUser(checked);
        if (checked) {
            setValue('bttUser', { bttUserDefaultLogin: true }, SHOULD_DIRTY_TRUE);
            setDefaultLogin(true);
        } else {
            setValue('bttUser', null, SHOULD_DIRTY_TRUE);
        }
    };

    const handleDefaultLoginChange = (_e, checked) => {
        setDefaultLogin(checked);
        if (checked) {
            // Clear credential fields when switching to default login
            setValue('bttUser.bttUserDomain', undefined, SHOULD_DIRTY_TRUE);
            setValue('bttUser.bttUsername', undefined, SHOULD_DIRTY_TRUE);
            setValue('bttUser.bttUserPassword', undefined, SHOULD_DIRTY_TRUE);
        }
    };

    return (
        <TestFormSection title={<Trans>Authentication</Trans>} addSpacer>
            {/* Client Certificate Authentication toggle */}
            <FormControlLabel
                control={<Switch checked={certAuthEnabled} onChange={handleCertAuthToggle} />}
                label={t`Client Certificate Authentication`}
            />

            {/* Certificate dropdown (visible when certAuth enabled) */}
            {certAuthEnabled && (
                <NgpSelect
                    fieldName="testConfig.auth.cert._id"
                    label={t`Certificate`}
                    disabled={isLoading}
                    className={classes.dynamicFieldLongWidth}
                    addEmptyOption
                    fullWidth
                >
                    {certificates.map((cert) => (
                        <MenuItem key={cert._id} value={cert._id}>
                            {cert.name}
                        </MenuItem>
                    ))}
                </NgpSelect>
            )}

            {/* Verify Server Certificate (inverted toggle) */}
            <NgpToggle
                fieldName="testConfig.disableServerCertVerification"
                label={t`Verify Server Certificate`}
                inverted
            />

            {/* Run script as user toggle */}
            <FormControlLabel
                control={<Switch checked={runAsUser} onChange={handleRunAsUserToggle} />}
                label={t`Run script as user (Windows nPoint only)`}
            />

            {/* Run As User sub-section (indented under toggle) */}
            {runAsUser && (
                <div className={classes.subSection}>
                    <NgpToggle
                        fieldName="bttUser.bttUserDefaultLogin"
                        label={t`Use Default nPoint Login Account`}
                        onChangeFunc={handleDefaultLoginChange}
                    />

                    {/* Credential fields (visible when not using default login) */}
                    {!defaultLogin && (
                        <>
                            <div style={{ width: 450 }}>
                                <NgpTextField
                                    fieldName="bttUser.bttUserDomain"
                                    label={t`Domain (Leave empty for local account)`}
                                    className={classes.dynamicFieldLongWidth}
                                    fullWidth
                                />
                            </div>
                            <div style={{ width: 450 }}>
                                <NgpTextField
                                    fieldName="bttUser.bttUsername"
                                    label={t`Username`}
                                    required
                                    inputProps={{
                                        size: 60,
                                        maxLength: 2083,
                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                    }}
                                />
                            </div>
                            <div style={{ width: 450 }}>
                                <NgpPasswordField
                                    fieldName="bttUser.bttUserPassword.value"
                                    label={t`Password`}
                                    inputProps={{ size: 60, maxLength: 2083 }}
                                    required
                                    togglePasswordVisibility
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </TestFormSection>
    );
}

TestBttCertificateSection.propTypes = {
    bttStateRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};
